import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { queries } from '~extension/web2print/controller/api/Queries';
import { JobDownloadFormat } from '~extension/web2print/model/Job';
import { DownloadDetail } from '~extension/web2print/view/Download/DownloadDetail';
import { makeProgramaticallyLinkClick } from '~utils/link';

type DownloadDetailControllerProps = {
  jobId: number;
  documentId: number;
  clientId: number;
  downloadFormat: JobDownloadFormat;
};

export function DownloadDetailController({
  jobId,
  documentId,
  clientId,
  downloadFormat,
}: DownloadDetailControllerProps) {
  const [downloadUrl, setDownloadUrl] = useState<string>(undefined);
  const [error, setError] = useState<boolean>(false);

  // check if job is open on o2e server
  const { data: jobIsOpen, isError: jobIsOpenError } = useQuery({
    ...queries.web2print.jobIsOpen(documentId, jobId, clientId, downloadFormat.id),
    enabled: !Boolean(error),
    initialData: true,
    refetchOnWindowFocus: false,
    refetchInterval: query => (!Boolean(query.state.data) ? false : 5 * 1000),
    gcTime: 1,
  });

  // get export ticket
  const { data: exportTicket, isError: exportTicketError } = useQuery({
    ...queries.web2print.getExportTicket(documentId, jobId, clientId, downloadFormat.id),
    enabled: !Boolean(error) && jobIsOpen !== undefined && Boolean(jobIsOpen) === false,
    refetchOnWindowFocus: false,
    gcTime: 0,
  });

  // start export job if no download formart id is avilable. (e.g. for running jobs)
  const { isError: exportJobError } = useQuery({
    ...queries.web2print.exportJob(jobId, clientId, exportTicket),
    enabled: !Boolean(error) && !Boolean(downloadFormat.id) && Boolean(exportTicket),
    refetchOnWindowFocus: false,
    gcTime: 0,
    retry: false,
  });

  // start export document if download formart id is avilable
  const { isError: exportDocumentError } = useQuery({
    ...queries.web2print.exportDocument(documentId, clientId, downloadFormat.id, exportTicket),
    enabled: !Boolean(error) && Boolean(downloadFormat.id) && Boolean(exportTicket),
    refetchOnWindowFocus: false,
    gcTime: 0,
    retry: false,
  });

  // check export status
  const { data: exportStatusData, isError: exportStatusError } = useQuery({
    ...queries.web2print.getExportStatus(exportTicket),
    enabled: !Boolean(error) && Boolean(exportTicket),
    refetchOnWindowFocus: false,
    gcTime: 0,
    refetchInterval: query => (query.state.data && query.state.data.status === 'finish' ? false : 5 * 1000),
  });

  useEffect(() => {
    if (exportStatusData !== undefined) setDownloadUrl(exportStatusData.url);
  }, [exportStatusData]);

  useEffect(() => {
    if (jobIsOpenError || exportTicketError || exportJobError || exportDocumentError || exportStatusError) {
      setError(true);
    }
  }, [jobIsOpenError, exportTicketError, exportJobError, exportDocumentError, exportStatusError]);

  // Handler: onDownload fetches download url and gives it back
  const onDownload = useCallback((href: string) => makeProgramaticallyLinkClick({ href }), []);

  return <DownloadDetail downloadUrl={downloadUrl} isLoading={!downloadUrl} onDownload={onDownload} error={error} />;
}
